// ============================================================================
// WIDGET LAUNCHER PANEL
// Facilitator tool: push interactive activities to participant devices.
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { Plus, X, Zap, Trash2, Loader, ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '../../services/api';

type WidgetType = 'mcq' | 'free_text' | 'rating';

interface WidgetResponse {
  participantId: string;
  participantName: string;
  answer: string;
  submittedAt: string;
}

interface WidgetSummary {
  widgetId: string;
  type: WidgetType;
  question: string;
  options?: string[];
  ratingMax?: number;
  pushedAt: string;
  closedAt: string | null;
  responseCount: number;
  tally: Record<string, number>;
  responses: WidgetResponse[];
}

interface Props {
  sessionId: string;
  fullPage?: boolean;
}

function TallyBar({ tally, total, type, responses }: {
  tally: Record<string, number>;
  total: number;
  type: WidgetType;
  responses: WidgetResponse[];
}) {
  if (type === 'free_text' && responses.length > 0) {
    return (
      <div className="space-y-1 mt-2">
        {responses.map(r => (
          <p key={r.participantId} className="text-slate-600 text-xs">
            <span className="text-slate-400">{r.participantName}: </span>{r.answer}
          </p>
        ))}
      </div>
    );
  }
  if (Object.keys(tally).length === 0) return null;
  return (
    <div className="space-y-1.5 mt-2">
      {Object.entries(tally).sort((a, b) => b[1] - a[1]).map(([answer, count]) => (
        <div key={answer} className="flex items-center gap-2">
          <span className="text-slate-600 text-xs truncate flex-1">{answer}</span>
          <div className="flex-1 bg-slate-200 rounded-full h-1.5 overflow-hidden">
            <div
              className="bg-primary h-full rounded-full transition-all"
              style={{ width: total > 0 ? `${(count / total) * 100}%` : '0%' }}
            />
          </div>
          <span className="text-slate-400 text-xs w-4 text-right">{count}</span>
        </div>
      ))}
    </div>
  );
}

export const WidgetLauncherPanel: React.FC<Props> = ({ sessionId, fullPage = false }) => {
  const [showForm, setShowForm] = useState(false);
  const [type, setType] = useState<WidgetType>('mcq');
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [pushing, setPushing] = useState(false);
  const [closing, setClosing] = useState<string | null>(null);
  const [widgets, setWidgets] = useState<WidgetSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const fetchWidgets = useCallback(async () => {
    try {
      const res = await api.get<{ widgets: WidgetSummary[] }>(`/api/sessions/${sessionId}/widgets`);
      setWidgets(res.data.widgets || []);
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchWidgets();
  }, [fetchWidgets]);

  // Poll while there's an active widget
  useEffect(() => {
    const hasActive = widgets.some(w => !w.closedAt);
    if (!hasActive) return;
    const interval = setInterval(fetchWidgets, 5000);
    return () => clearInterval(interval);
  }, [widgets, fetchWidgets]);

  const activeWidget = widgets.find(w => !w.closedAt) ?? null;
  const history = widgets.filter(w => w.closedAt);

  async function pushWidget() {
    if (!question.trim()) return;
    setPushing(true);
    try {
      const body: any = { type, question: question.trim() };
      if (type === 'mcq') body.options = options.filter(o => o.trim());
      await api.post(`/api/sessions/${sessionId}/widgets`, body);
      setShowForm(false);
      setQuestion('');
      setOptions(['', '']);
      await fetchWidgets();
    } catch { /* silent */ } finally {
      setPushing(false);
    }
  }

  async function closeWidget(widgetId: string) {
    setClosing(widgetId);
    try {
      await api.delete(`/api/sessions/${sessionId}/widgets/${widgetId}`);
      await fetchWidgets();
    } catch { /* silent */ } finally {
      setClosing(null);
    }
  }

  function toggleExpand(id: string) {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  const p = fullPage ? 'p-5' : 'p-3';

  return (
    <div className={fullPage ? 'space-y-4' : 'border-t border-slate-200 pt-3 mt-3 space-y-3'}>
      {/* Header */}
      <div className="flex items-center justify-between">
        {!fullPage && (
          <div className="flex items-center gap-2">
            <Zap size={14} className="text-primary" />
            <span className="text-slate-500 text-xs font-medium uppercase tracking-wider">Activity</span>
          </div>
        )}
        {!activeWidget && (
          <button
            onClick={() => setShowForm(f => !f)}
            className={`flex items-center gap-1 font-medium text-primary hover:text-primary/80 ${fullPage ? 'text-sm px-4 py-2 bg-primary/10 rounded-lg' : 'text-xs ml-auto'}`}
          >
            <Plus size={fullPage ? 14 : 12} />
            {showForm ? 'Cancel' : 'New Activity'}
          </button>
        )}
      </div>

      {/* Create form */}
      {showForm && !activeWidget && (
        <div className={`bg-slate-50 border border-slate-200 rounded-xl space-y-3 ${p}`}>
          <div className="flex gap-1">
            {(['mcq', 'free_text', 'rating'] as WidgetType[]).map(t => (
              <button
                key={t}
                onClick={() => setType(t)}
                className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  type === t ? 'bg-primary text-white' : 'bg-white border border-slate-200 text-slate-600'
                }`}
              >
                {t === 'mcq' ? 'Choice' : t === 'free_text' ? 'Text' : 'Rating'}
              </button>
            ))}
          </div>
          <textarea
            value={question}
            onChange={e => setQuestion(e.target.value)}
            placeholder="Question…"
            rows={2}
            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-slate-800 placeholder-slate-400 text-xs focus:outline-none focus:border-primary resize-none"
          />
          {type === 'mcq' && (
            <div className="space-y-1.5">
              {options.map((opt, i) => (
                <div key={`opt-${i}-${opt}`} className="flex gap-1.5">
                  <input
                    value={opt}
                    onChange={e => setOptions(prev => prev.map((o, j) => j === i ? e.target.value : o))}
                    placeholder={`Option ${i + 1}`}
                    className="flex-1 bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-slate-800 placeholder-slate-400 text-xs focus:outline-none focus:border-primary"
                  />
                  {options.length > 2 && (
                    <button onClick={() => setOptions(prev => prev.filter((_, j) => j !== i))} className="text-slate-300 hover:text-red-400">
                      <X size={12} />
                    </button>
                  )}
                </div>
              ))}
              {options.length < 4 && (
                <button onClick={() => setOptions(prev => [...prev, ''])} className="text-primary text-xs font-medium flex items-center gap-1">
                  <Plus size={10} /> Add option
                </button>
              )}
            </div>
          )}
          <button
            onClick={pushWidget}
            disabled={pushing || !question.trim()}
            className="w-full bg-primary text-white font-semibold py-2 rounded-lg text-xs disabled:opacity-40 flex items-center justify-center gap-2"
          >
            {pushing && <Loader size={12} className="animate-spin" />}
            Push to devices
          </button>
        </div>
      )}

      {/* Active widget */}
      {activeWidget && (
        <div className={`bg-primary/5 border border-primary/20 rounded-xl ${p}`}>
          <div className="flex items-start justify-between gap-2 mb-1">
            <div>
              <span className="inline-flex items-center gap-1 text-primary text-xs font-medium mb-1">
                <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse inline-block" />
                Live · {activeWidget.responseCount} response{activeWidget.responseCount !== 1 ? 's' : ''}
              </span>
              <p className="text-slate-800 text-sm font-medium">{activeWidget.question}</p>
            </div>
            <button
              onClick={() => closeWidget(activeWidget.widgetId)}
              disabled={closing === activeWidget.widgetId}
              className="text-slate-400 hover:text-red-400 transition-colors flex-shrink-0 mt-0.5"
              title="Close activity"
            >
              {closing === activeWidget.widgetId ? <Loader size={14} className="animate-spin" /> : <Trash2 size={14} />}
            </button>
          </div>
          <TallyBar tally={activeWidget.tally} total={activeWidget.responseCount} type={activeWidget.type} responses={activeWidget.responses} />
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="space-y-2">
          {fullPage && <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Previous Activities</p>}
          {history.map(w => {
            const expanded = expandedIds.has(w.widgetId);
            return (
              <div key={w.widgetId} className={`bg-slate-50 border border-slate-200 rounded-xl ${p}`}>
                <button
                  onClick={() => toggleExpand(w.widgetId)}
                  className="w-full flex items-start justify-between gap-2 text-left"
                >
                  <div className="min-w-0">
                    <p className="text-slate-700 text-xs font-medium truncate">{w.question}</p>
                    <p className="text-slate-400 text-xs mt-0.5">{w.responseCount} response{w.responseCount !== 1 ? 's' : ''}</p>
                  </div>
                  {expanded ? <ChevronUp size={14} className="text-slate-400 flex-shrink-0 mt-0.5" /> : <ChevronDown size={14} className="text-slate-400 flex-shrink-0 mt-0.5" />}
                </button>
                {expanded && (
                  <div className="mt-2 pt-2 border-t border-slate-200">
                    <TallyBar tally={w.tally} total={w.responseCount} type={w.type} responses={w.responses} />
                    {w.responseCount === 0 && <p className="text-slate-400 text-xs">No responses</p>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!loading && widgets.length === 0 && !showForm && (
        <p className="text-slate-400 text-xs">No activities yet</p>
      )}
    </div>
  );
};
